/**
 * The Waterway validation schedule (waterways-validation.pdf, section 3):
 *
 *   ten sessions over five weeks, two sessions per week, one attention mode (level) per week, with the order
 *   of the five modes counterbalanced across participants by a Latin square.
 *
 * Session n (1-based) falls in week ⌈n / sessionsPerMode⌉. Subjects are assigned a Latin-square row by
 * subject ID, row = (subjectId − 1) mod 5, and in week w (0-based) they play mode (row + w) mod 5. Across any five
 * consecutive subject IDs every mode appears exactly once in every week.
 *
 * Everything here is pure so the server, the browser and the tests share one definition.
 */

export type ModeId = "l1" | "l2" | "l3" | "l4" | "l5";

/** The five modes in the program's attention hierarchy order; the ids are the Waterway level ids. */
export const MODES: readonly ModeId[] = ["l1", "l2", "l3", "l4", "l5"];

export interface ScheduleConfig {
  /** Sessions in the whole program (default 10). */
  totalSessions: number;
  /** Consecutive sessions spent on one mode (default 2: two sessions per week, one mode per week). */
  sessionsPerMode: number;
}

export const DEFAULT_SCHEDULE: ScheduleConfig = Object.freeze({ totalSessions: 10, sessionsPerMode: 2 });

export interface ScheduledSession {
  /** 1-based session number within the program. */
  number: number;
  /** 1-based block (week) of the program. */
  week: number;
  /** Which of the sessions on this mode this is (1-based). */
  sessionInWeek: number;
  /** Waterway level id to play. */
  levelId: ModeId;
}

/** Latin-square row for a subject: consecutive subject IDs cycle through the five rows. */
export function latinSquareRow(subjectId: number): number {
  return (subjectId - 1) % MODES.length;
}

/** The mode order a subject follows across the program's weeks. */
export function modeOrder(subjectId: number): ModeId[] {
  const row = latinSquareRow(subjectId);
  return MODES.map((_, w) => MODES[(row + w) % MODES.length]!);
}

export function isValidSubjectId(v: unknown): v is number {
  return typeof v === "number" && Number.isSafeInteger(v) && v >= 1;
}

/** Parses a subject ID typed by a person: digits only, 1 … 2^53 − 1 (the upload API's subject_id range). */
export function parseSubjectId(text: string): number | null {
  const t = text.trim();
  if (!/^[1-9]\d{0,15}$/.test(t)) return null;
  const n = Number(t);
  return isValidSubjectId(n) ? n : null;
}

/** The session with the given number for a subject, or null when the number is outside the program. */
export function scheduledSession(subjectId: number, number: number, cfg: ScheduleConfig = DEFAULT_SCHEDULE): ScheduledSession | null {
  if (!isValidSubjectId(subjectId) || !Number.isInteger(number) || number < 1 || number > cfg.totalSessions) return null;
  const weekIndex = Math.floor((number - 1) / cfg.sessionsPerMode);
  return {
    number,
    week: weekIndex + 1,
    sessionInWeek: ((number - 1) % cfg.sessionsPerMode) + 1,
    levelId: modeOrder(subjectId)[weekIndex % MODES.length]!,
  };
}

/** The full program for a subject. */
export function fullSchedule(subjectId: number, cfg: ScheduleConfig = DEFAULT_SCHEDULE): ScheduledSession[] {
  const out: ScheduledSession[] = [];
  for (let n = 1; n <= cfg.totalSessions; n++) out.push(scheduledSession(subjectId, n, cfg)!);
  return out;
}

export function validateScheduleConfig(cfg: ScheduleConfig): ScheduleConfig {
  if (!Number.isInteger(cfg.totalSessions) || cfg.totalSessions < 1 || cfg.totalSessions > 1000) {
    throw new Error(`totalSessions must be an integer from 1 to 1000 (got ${cfg.totalSessions})`);
  }
  if (!Number.isInteger(cfg.sessionsPerMode) || cfg.sessionsPerMode < 1) {
    throw new Error(`sessionsPerMode must be a positive integer (got ${cfg.sessionsPerMode})`);
  }
  return cfg;
}
